import { ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { AppTopbar, RoleBadge } from './AppTopbar';
import { AppMobileBottomNav } from './AppMobileBottomNav';
import { InlineBanner } from './InlineBanner';
import { ProducerFAB } from '@/components/producer/ProducerFAB';
import { ShopMobileSectionNav } from './ShopMobileSectionNav';
import { AdminMobileSectionNav } from './AdminMobileSectionNav';

import { supabase } from '@/integrations/supabase/client';
import { useContainerBreakpoint } from '@/hooks/useContainerBreakpoint';
import type { NavConfig, NavBadgeCounts } from '@/config/nav.types';
import type { ProducerCapabilities as RoleCapabilities } from '@/hooks/useProducerAccess';
import type { ProducerMeta, ProducerCapabilityKey } from '@/lib/capabilities';
import type { ProducerFeatureNavItem } from '@/config/producerFeatures';
import type { EffectiveFeatureMap } from '@/hooks/useProducerEffectiveFeatures';

// ── hideChrome patterns ──────────────────────────────────────
// Only routes that genuinely need a blank canvas (no sidebar, no topbar).
const HIDE_CHROME_PATTERNS = [
  /\/producer\/farm\/[^/]+\/terms$/,
];

/** Routes where bottom nav and FAB are hidden but topbar/sidebar remain.
 *  Editor pages typically render their own sticky footer on mobile. */
const HIDE_BOTTOM_NAV_PATTERNS: RegExp[] = [
  /\/events\/new$/,
  /\/events\/[^/]+\/edit$/,
  /\/outings\/new$/,
  /\/outings\/[^/]+$/,
  /\/shop\/products\/new$/,
  /\/shop\/products\/[^/]+\/edit$/,
  /\/shop\/products\/[^/]+$/,
];

function shouldHideChrome(pathname: string): boolean {
  return HIDE_CHROME_PATTERNS.some((re) => re.test(pathname));
}

interface AppShellProps {
  children: ReactNode;
  navConfig: NavConfig;
  role: RoleBadge;
  title?: string;
  contextTitle?: string;
  backHref?: string;
  backLabel?: string;
  enabledCapabilities?: string[];
  currentProducerId?: string;
  currentProducerName?: string;
  showAllWithStatusBadges?: boolean;
  badgeCounts?: NavBadgeCounts;
  roleCapabilities?: RoleCapabilities;
  isViewerRole?: boolean;
  producerMeta?: ProducerMeta | null;
  capabilitiesMap?: Record<ProducerCapabilityKey, boolean> | null;
  featureNavItems?: ProducerFeatureNavItem[];
  effectiveFeatures?: EffectiveFeatureMap;
  featureBaseHref?: string;
  /** Override footer nav items (e.g. producer portal 5-tab footer) */
  footerOverride?: import('@/config/nav.types').NavItem[];
}

export function AppShell({
  children,
  navConfig,
  role,
  title,
  contextTitle,
  backHref,
  backLabel,
  enabledCapabilities = [],
  currentProducerId,
  currentProducerName,
  showAllWithStatusBadges = false,
  badgeCounts = {},
  roleCapabilities,
  isViewerRole = false,
  producerMeta,
  capabilitiesMap,
  featureNavItems = [],
  effectiveFeatures = {},
  featureBaseHref = '',
  footerOverride,
}: AppShellProps) {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { pathname } = useLocation();
  const shellRef = useRef<HTMLDivElement>(null);

  // Container-width breakpoint: replaces viewport-based md:hidden.
  // This ensures the bottom nav shows when the layout container is narrow,
  // even if the desktop viewport is wide (e.g. embedded preview iframe).
  const isNarrow = useContainerBreakpoint(shellRef, 768);

  useEffect(() => {
    const hydrateEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserEmail(session?.user.email ?? null);
    };
    hydrateEmail();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── hideChrome for editor/fullscreen routes ────────────────
  const hideChrome = shouldHideChrome(pathname);
  const hideBottomNav = HIDE_BOTTOM_NAV_PATTERNS.some((re) => re.test(pathname));

  if (hideChrome) {
    return (
      <div className="min-h-dvh w-full max-w-full overflow-x-hidden bg-background">
        {children}
      </div>
    );
  }

  const showBottomNav = isNarrow && !hideBottomNav;

  return (
    <SidebarProvider>
      {/* Shell root: ref for container-width measurement.
          --app-bottom-nav-h token centralises the nav height for pb + FAB calculations. */}
      <div
        ref={shellRef}
        className="flex min-h-[100dvh] w-full min-w-0 max-w-full overflow-x-clip"
        style={{ '--app-bottom-nav-h': showBottomNav ? '72px' : '0px' } as React.CSSProperties}
      >
        <AppSidebar
          navConfig={navConfig}
          contextTitle={contextTitle}
          backHref={backHref}
          backLabel={backLabel}
          enabledCapabilities={enabledCapabilities}
          showAllWithStatusBadges={showAllWithStatusBadges}
          badgeCounts={badgeCounts}
          roleCapabilities={roleCapabilities}
          isViewerRole={isViewerRole}
          producerMeta={producerMeta}
          capabilitiesMap={capabilitiesMap}
          featureNavItems={featureNavItems}
          effectiveFeatures={effectiveFeatures}
          featureBaseHref={featureBaseHref}
        />
        <SidebarInset>
          <AppTopbar
            title={title}
            role={role}
            userEmail={userEmail}
            currentProducerId={currentProducerId}
            currentProducerName={currentProducerName}
            isNarrow={isNarrow}
          />
          <InlineBanner />
           {/* Mobile-only shop section switcher */}
           {isNarrow && role === 'producer' && <ShopMobileSectionNav />}
           {/* Mobile-only admin section switcher */}
           {isNarrow && role === 'admin' && <AdminMobileSectionNav badgeCounts={badgeCounts} />}
          {/* Content wrapper: <div> to avoid nested <main> inside SidebarInset's <main>.
              pb uses CSS var token so nav height is defined in one place. */}
          <div
            className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-y-auto overflow-x-hidden px-4 md:px-8 lg:pl-12 lg:pr-8 pb-[calc(var(--app-bottom-nav-h,0px)+env(safe-area-inset-bottom,0px))]"
          >
            {children}
          </div>
        </SidebarInset>
      </div>

      {/* Fixed elements: OUTSIDE the scrolling flex wrapper.
          Rendered conditionally based on container width, not viewport. */}
      {showBottomNav && (
        <AppMobileBottomNav
          navConfig={navConfig}
          role={role}
          badgeCounts={badgeCounts}
          footerOverride={footerOverride}
          visibilityCtx={{
            enabledCapabilities,
            roleCapabilities,
            isViewerRole,
            producerMeta,
            capabilitiesMap,
            showAllWithStatusBadges,
          }}
        />
      )}

      {/* Producer FAB — also only in narrow layout, hidden on onboarding */}
      {showBottomNav && role === 'producer' && (
        <ProducerFAB farmId={currentProducerId} />
      )}
    </SidebarProvider>
  );
}
